<template>
  <div class="pa-explore">
    <div class="pa-explore-eye">{{ t('property.access.guestExploreNearby.eyebrow') }}</div>
    <h3 class="pa-explore-title">{{ t('property.access.guestExploreNearby.title', { area }) }}</h3>
    <ul
      v-if="nearby.length"
      class="pa-explore-list"
    >
      <li
        v-for="item in nearby"
        :key="item.title"
      >
        <button
          type="button"
          class="row"
          @click="emit('item-click', item.title)"
        >
          <span class="dot" />
          <span class="t">{{ item.title }}</span>
          <span class="m">{{
            t('property.access.guestExploreNearby.itemMeta', {
              amount: formatFromAed(item.fromAed),
              quarter: item.quarter,
            })
          }}</span>
        </button>
      </li>
    </ul>
    <div
      v-else
      class="pa-explore-empty"
    >
      {{ t('property.access.guestExploreNearby.empty') }}
    </div>
  </div>
</template>

<script setup lang="ts">
const { t } = useI18n();

defineProps<{
  area: string;
  nearby: { title: string; fromAed: number; quarter: string }[];
}>();

const emit = defineEmits<{
  (e: 'item-click', title: string): void;
}>();

// Compact AED formatter (1.2M/450K abbreviations) — kept as English-only
// display data, matching the precedent set for compact AED formatters elsewhere
// in the property/portfolio surfaces (see report).
const formatFromAed = (n: number) => {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n >= 1_000_000) {
    const m = Math.floor(n / 100_000) / 10;
    return `${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (n >= 1_000) {
    const k = Math.floor(n / 1_000);
    return `${k}K`;
  }
  return String(Math.floor(n));
};
</script>

<style scoped lang="scss">
.pa-explore {
  background: white;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 16px;
  padding: 20px 18px;
}

.pa-explore-eye {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(13, 15, 21, 0.62);
  margin-bottom: 4px;
}

.pa-explore-title {
  font-family: 'Fraunces', 'Times New Roman', serif;
  font-weight: 600;
  font-size: 18px;
  letter-spacing: -0.015em;
  margin: 0 0 14px;
  color: #0d0f15;
}

.pa-explore-list {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    border-top: 1px solid rgba(13, 15, 21, 0.08);

    &:first-child {
      border-top: none;
    }
  }

  .row {
    display: grid;
    grid-template-columns: 14px 1fr;
    grid-template-rows: auto auto;
    column-gap: 8px;
    width: 100%;
    padding: 10px 0;
    background: none;
    border: none;
    text-align: start;
    cursor: pointer;

    &:hover .t {
      text-decoration: underline;
    }
  }

  li:first-child .row {
    padding-top: 0;
  }

  .dot {
    grid-row: 1 / 3;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ffb648;
    margin-top: 6px;
  }

  .t {
    font-size: 13.5px;
    font-weight: 700;
    color: #0d0f15;
  }

  .m {
    font-size: 12px;
    color: rgba(13, 15, 21, 0.62);
    margin-top: 2px;
  }
}

.pa-explore-empty {
  font-size: 12.5px;
  color: rgba(13, 15, 21, 0.62);
  padding: 6px 0 2px;
}
</style>
